<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Crew Management</h1>
            <p class="text-slate-400">Manage your contractors and team members</p>
          </div>
          <button @click="showAddCrewModal = true" class="btn-primary">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            Add Crew Member
          </button>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Crew</p>
            <p class="text-3xl font-bold text-white">{{ crewStats.totalCrew }}</p>
            <p class="text-sm text-emerald-400">members</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Active Jobs</p>
            <p class="text-3xl font-bold text-white">{{ crewStats.activeJobs }}</p>
            <p class="text-sm text-cyan-400">currently assigned</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Certified Crew</p>
            <p class="text-3xl font-bold text-white">{{ crewStats.certifiedCrew }}</p>
            <p class="text-sm text-amber-400">up to date</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Performance</p>
            <p class="text-3xl font-bold text-white">{{ crewStats.performance }}%</p>
            <p class="text-sm text-purple-400">average rating</p>
          </div>
        </div>
      </div>

      <!-- Filters and Search -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center space-x-4">
          <select v-model="crewFilter" class="form-select">
            <option value="all">All Crew</option>
            <option value="active">Active</option>
            <option value="available">Available</option>
            <option value="on-job">On Job</option>
            <option value="certified">Certified</option>
          </select>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Search crew members..."
            class="form-input"
          />
        </div>
        <div class="flex items-center space-x-3">
          <button @click="exportCrewData" class="btn-secondary">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            Export
          </button>
        </div>
      </div>

      <!-- Crew List -->
      <div v-if="isLoading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>

      <div v-else-if="filteredCrew.length === 0" class="text-center py-12">
        <div class="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-white mb-2">No crew members found</h3>
        <p class="text-slate-400 mb-4">Start building your team by adding crew members</p>
        <button @click="showAddCrewModal = true" class="btn-primary">
          Add Your First Crew Member
        </button>
      </div>

      <div v-else class="grid gap-6">
        <div 
          v-for="member in filteredCrew" 
          :key="member.id"
          class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-800/70 transition-colors"
        >
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-start space-x-4">
              <div class="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                <span class="text-white font-semibold text-lg">{{ member.name.charAt(0) }}</span>
              </div>
              <div class="flex-1">
                <div class="flex items-center space-x-3 mb-2">
                  <h3 class="text-xl font-semibold text-white">{{ member.name }}</h3>
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    member.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                    member.status === 'available' ? 'bg-blue-500/20 text-blue-400' :
                    member.status === 'on-job' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-slate-500/20 text-slate-400'
                  ]">
                    {{ member.status }}
                  </span>
                </div>
                <p class="text-slate-400 mb-3">{{ member.role }}</p>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span class="text-slate-400">Experience:</span>
                    <span class="text-white ml-1">{{ member.experience_years }} years</span>
                  </div>
                  <div>
                    <span class="text-slate-400">Location:</span>
                    <span class="text-white ml-1">{{ member.location }}</span>
                  </div>
                  <div>
                    <span class="text-slate-400">Certifications:</span>
                    <span class="text-white ml-1">{{ member.certifications_count }}</span>
                  </div>
                  <div>
                    <span class="text-slate-400">Rating:</span>
                    <span class="text-white ml-1">{{ member.rating }}/5</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button @click="viewProfile(member.id)" class="btn-secondary">
                View Profile
              </button>
              <button @click="assignJob(member.id)" class="btn-primary">
                Assign Job
              </button>
            </div>
          </div>
          
          <div class="flex items-center justify-between pt-4 border-t border-slate-700/50">
            <div class="text-sm text-slate-400">
              Joined {{ formatDate(member.joined_date) }}
            </div>
            <div class="flex items-center space-x-3">
              <button @click="editMember(member.id)" class="btn-secondary">
                Edit
              </button>
              <button @click="removeMember(member.id)" class="btn-danger">
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Crew Modal -->
      <div v-if="showAddCrewModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
          <h3 class="text-xl font-semibold text-white mb-4">Add Crew Member</h3>
          
          <form @submit.prevent="addCrewMember" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Name</label>
              <input v-model="newMember.name" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Role</label>
              <select v-model="newMember.role" class="form-select w-full" required>
                <option value="">Select Role</option>
                <option value="Driller">Driller</option>
                <option value="Rig Manager">Rig Manager</option>
                <option value="Safety Officer">Safety Officer</option>
                <option value="Equipment Operator">Equipment Operator</option>
                <option value="Maintenance Technician">Maintenance Technician</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Experience (years)</label>
              <input v-model="newMember.experience_years" type="number" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Location</label>
              <input v-model="newMember.location" type="text" class="form-input w-full" required />
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showAddCrewModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Add Member
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const isLoading = ref(false)
const crewFilter = ref('all')
const searchQuery = ref('')
const showAddCrewModal = ref(false)

const crewStats = ref({
  totalCrew: 0,
  activeJobs: 0,
  certifiedCrew: 0,
  performance: 0
})

const crew = ref([])

const newMember = ref({
  name: '',
  role: '',
  experience_years: 0,
  location: ''
})

// Computed properties
const filteredCrew = computed(() => {
  let filtered = crew.value
  
  if (crewFilter.value !== 'all') {
    filtered = filtered.filter(member => member.status === crewFilter.value)
  }
  
  if (searchQuery.value) {
    filtered = filtered.filter(member => 
      member.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      member.role.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  }
  
  return filtered
})

// Methods
const fetchCrewStats = async () => {
  try {
    const response = await fetch('/api/company/crew-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      crewStats.value = data
    }
  } catch (error) {
    console.error('Error fetching crew stats:', error)
  }
}

const fetchCrew = async () => {
  try {
    isLoading.value = true
    const response = await fetch('/api/company/crew', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      crew.value = data.crew || []
    }
  } catch (error) {
    console.error('Error fetching crew:', error)
  } finally {
    isLoading.value = false
  }
}

const addCrewMember = async () => {
  try {
    const response = await fetch('/api/company/crew', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newMember.value)
    })
    
    if (response.ok) {
      showAddCrewModal.value = false
      newMember.value = { name: '', role: '', experience_years: 0, location: '' }
      await fetchCrew()
      await fetchCrewStats()
    }
  } catch (error) {
    console.error('Error adding crew member:', error)
  }
}

const viewProfile = (memberId: number) => {
  // Navigate to member profile
}

const assignJob = (memberId: number) => {
  // Open job assignment modal
}

const editMember = (memberId: number) => {
  // Open edit modal
}

const removeMember = async (memberId: number) => {
  if (!window.confirm('Remove this crew member? This cannot be undone.')) return
  try {
    const response = await fetch(`/api/company/crew/${memberId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json',
      },
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
    crew.value = crew.value.filter((member: any) => member.id !== memberId)
    await fetchCrewStats()
  } catch (error) {
    console.error('Error removing crew member:', error)
  }
}

const exportCrewData = () => {
  // Export crew data to CSV
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchCrewStats(),
    fetchCrew()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors;
}

.btn-danger {
  @apply px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium rounded-lg transition-colors;
}

.form-select, .form-input {
  @apply bg-slate-800/50 border border-slate-700/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}

.form-input {
  @apply min-w-[200px];
}
</style>
